import React, { useEffect } from "react";
import { LogLevel } from "@grafana/faro-web-sdk";
import { startClickSpan, TraceEntry } from "../../utils/traceContextStore";

const debounce = (func: any, delay: number) => {
  let timeoutId: any;
  return (...args: any) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const findText = (el: any) =>
  el?.innerText ||
  el?.textContent ||
  el?.label ||
  el?.ariaLabel ||
  el?.getAttribute("data-testid") ||
  "";

const pushLog = (message: string, spanCtx: TraceEntry | null) => {
  const { faro } = window as any;
  if (faro) {
    faro.api.pushLog([message], {
      level: LogLevel.INFO,
      ...(spanCtx ? { traceContext: spanCtx } : {})
    });
  }
};

const handleClick = (event: any) => {
  let element = event?.target;

  while (element) {
    const tagName = element?.tagName?.toLowerCase();
    if (tagName?.match(/^(button|a|input|select|textarea|span|li|h1|h2|h3|h4|h5)$/i)) {
      break;
    }
    if (tagName === "svg") {
      const parent = element?.parentElement;
      if (parent?.tagName?.toLowerCase() !== "button") {
        break;
      }
    }
    element = element?.parentElement;
  }

  if (element) {
    const elementType = element?.tagName?.toLowerCase();
    const elementId = element?.id ? `#${element?.id}` : "";

    let elementText = findText(element);

    if (!elementText) {
      for (let i = 0; i < Math.min(2, element?.children?.length) && !elementText; i++) {
        elementText = findText(element.children[i]);
      }
      let parentElement = element?.parentElement;
      for (let i = 0; i < 2 && !elementText && parentElement; i++) {
        elementText = findText(parentElement);
        parentElement = parentElement?.parentElement;
      }
    }

    const logMessage = `User clicked on ${elementType}${elementId ? `: ${elementId}` : ""}${elementText ? `: ${elementText}` : ""}`;
    console.log(logMessage);

    const spanCtx = startClickSpan(`ui.click: ${elementType}${elementText ? `: ${elementText.slice(0, 60)}` : ""}`);
    pushLog(logMessage, spanCtx);
  } else {
    console.log("No valid element found for logging.");
  }
};

const handleInput = debounce((event: any) => {
  const input = event?.target;
  const logMessage = `Input changed: ${input?.name || input?.id} to ${input?.value}`;
  console.log(logMessage);
  pushLog(logMessage, null);
}, 300);

const handleVisibilityChange = () => {
  if (document?.hidden) {
    const logMessage = "User switched to another tab or minimized the browser";
    console.log(logMessage);
    pushLog(logMessage, null);
  } else {
    const logMessage = "User returned to the tab";
    console.log(logMessage);
    pushLog(logMessage, null);
  }
};

const handleBeforeUnload = () => {
  pushLog("User is leaving the page", null);
};

const withUserInteractionTracking = (WrappedComponent: any) => (props: any) => {
  useEffect(() => {
    document.addEventListener("click", handleClick);
    document.addEventListener("input", handleInput);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("input", handleInput);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return <WrappedComponent {...props} />;
};

export default withUserInteractionTracking;
